// ── BCH Live — Stream Discovery ───────────────────────────────────
//
// One place that owns "what streams exist and how good are they".
//
// Two queries only, both relay-filtered:
//   1. kind 30311 stream events (the directory itself)
//   2. kind 1311 chat + kind 9735 tips + kind 7 reactions + kind 6/16
//      reposts, batched into a single filter set, used to score streams
//
// Everything else (bucketing into live/upcoming/ended, ranking,
// category and search filtering) is derived with memos so the page can
// re-render cheaply.

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { Event } from 'nostr-tools';
import { useNostrQuery } from './useNostrQuery';
import {
  STREAM_EVENT_KIND, LIVE_CHAT_KIND, parseStreamEvent, slog,
} from '../lib/streamCredentials';
import {
  toLiveStream, scoreStream, EMPTY_SIGNALS,
  type LiveStream, type StreamSignals,
} from '../lib/liveStream';
import { getBCHPrice } from './useBCHPrice';
import { TIP_RECEIPT_KIND } from './useStreamTips';

const REACTION_KIND = 7;

/** Window for both stream discovery and signal collection. */
const LOOKBACK_SECS = 86400 * 3;

export interface DiscoveryBuckets {
  live: LiveStream[];
  upcoming: LiveStream[];
  ended: LiveStream[];
  featured: LiveStream | null;
  trending: LiveStream[];
  all: LiveStream[];
}

export interface UseStreamDiscoveryResult extends DiscoveryBuckets {
  loading: boolean;
  refetch: () => void;
  signalsFor: (aTag: string) => StreamSignals;
  scoreFor: (aTag: string) => number;
  /** Distinct categories present in the current result set, with counts */
  categoryCounts: Map<string, number>;
  totalTippedUsd: number;
}

/** Parse a tip receipt's USD value, falling back to sats × price. */
function tipUsd(ev: Event): number {
  try {
    const d = JSON.parse(ev.content) as { amount_usd?: number; amount_sats?: number };
    if (typeof d.amount_usd === 'number' && d.amount_usd > 0) return d.amount_usd;
    if (typeof d.amount_sats === 'number' && d.amount_sats > 0) {
      const p = getBCHPrice();
      if (p) return (d.amount_sats / 1e8) * p;
    }
  } catch { /* not JSON — fall through to the amount tag */ }
  const amt = ev.tags.find(t => t[0] === 'amount')?.[1];
  if (amt) {
    const sats = parseInt(amt, 10);
    if (Number.isFinite(sats) && sats > 0) {
      const p = getBCHPrice();
      if (p) return (sats / 1e8) * p;
    }
  }
  return 0;
}

/** The `a` coordinate an engagement event points at, if any. */
function targetATag(ev: Event): string | null {
  const a = ev.tags.find(t => t[0] === 'a')?.[1];
  if (a && a.startsWith(`${STREAM_EVENT_KIND}:`)) return a;
  return null;
}

export function useStreamDiscovery(): UseStreamDiscoveryResult {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - LOOKBACK_SECS);

  // ── 1. The stream directory ──
  // `bch-live` is our own tag; the broader tags catch streams from
  // creators who tagged loosely, and the untagged query catches the
  // rest of the NIP-53 ecosystem so BCHNostr stays a good citizen.
  const streamFilters = useMemo(() => [
    { kinds: [STREAM_EVENT_KIND], '#t': ['bch-live'], limit: 100, since },
    { kinds: [STREAM_EVENT_KIND], '#t': ['bch'], limit: 60, since },
    { kinds: [STREAM_EVENT_KIND], '#t': ['bitcoincash'], limit: 60, since },
    { kinds: [STREAM_EVENT_KIND], limit: 120, since },
  ], [since]);

  const { events: streamEvents, loading, refetch } = useNostrQuery({
    filters: streamFilters,
    allRelays: true,
  });

  // Only show the skeleton on the very first load, never on refetch.
  const loadedOnceRef = useRef(false);
  if (!loading) loadedOnceRef.current = true;
  const showLoading = loading && !loadedOnceRef.current;

  // ── Collapse to newest event per (pubkey, d) and enrich ──
  const all = useMemo(() => {
    const byKey = new Map<string, Event>();
    for (const ev of streamEvents) {
      const d = ev.tags.find(t => t[0] === 'd')?.[1];
      if (!d) continue;
      const key = `${ev.pubkey}:${d}`;
      const prev = byKey.get(key);
      if (!prev || ev.created_at > prev.created_at) byKey.set(key, ev);
    }

    const out: LiveStream[] = [];
    for (const ev of byKey.values()) {
      const meta = parseStreamEvent(ev);
      // A stream with no playable URL and no schedule is noise.
      if (!meta.streamingUrl && meta.status !== 'planned') continue;
      out.push(toLiveStream(meta, ev.tags));
    }
    slog('discovery', `${streamEvents.length} raw → ${out.length} unique stream(s)`);
    return out;
  }, [streamEvents]);

  // ── 2. Engagement signals ──
  // Scoped to the `a` coordinates we actually have, so relays do the
  // filtering. Capped to keep the filter payload sane on big directories.
  const aTags = useMemo(
    () => all.slice(0, 60).map(s => s.aTag).filter(Boolean),
    [all],
  );
  const aTagKey = aTags.join(',');

  const signalFilters = useMemo(() => {
    if (aTags.length === 0) return [];
    return [
      { kinds: [LIVE_CHAT_KIND], '#a': aTags, limit: 500, since },
      { kinds: [TIP_RECEIPT_KIND], '#a': aTags, limit: 300, since },
      { kinds: [REACTION_KIND], '#a': aTags, limit: 300, since },
      { kinds: [6, 16], '#a': aTags, limit: 100, since },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aTagKey, since]);

  const { events: signalEvents } = useNostrQuery({
    filters: signalFilters,
    enabled: aTags.length > 0,
  });

  // ── Aggregate signals per stream coordinate ──
  const { signalMap, totalTippedUsd } = useMemo(() => {
    const map = new Map<string, StreamSignals>();
    const chatters = new Map<string, Set<string>>();
    let totalUsd = 0;

    const ensure = (a: string): StreamSignals => {
      let s = map.get(a);
      if (!s) { s = { ...EMPTY_SIGNALS }; map.set(a, s); }
      return s;
    };

    for (const ev of signalEvents) {
      const a = targetATag(ev);
      if (!a) continue;
      const s = ensure(a);

      if (ev.kind === LIVE_CHAT_KIND) {
        s.chatCount++;
        let set = chatters.get(a);
        if (!set) { set = new Set(); chatters.set(a, set); }
        set.add(ev.pubkey);
      } else if (ev.kind === TIP_RECEIPT_KIND) {
        s.tipCount++;
        const usd = tipUsd(ev);
        s.tipUsd += usd;
        totalUsd += usd;
      } else if (ev.kind === REACTION_KIND) {
        s.reactionCount++;
      } else if (ev.kind === 6 || ev.kind === 16) {
        s.shareCount++;
      }
    }

    for (const [a, set] of chatters) {
      const s = map.get(a);
      if (s) s.uniqueChatters = set.size;
    }

    return { signalMap: map, totalTippedUsd: totalUsd };
  }, [signalEvents]);

  const signalsFor = useCallback(
    (aTag: string): StreamSignals => signalMap.get(aTag) || EMPTY_SIGNALS,
    [signalMap],
  );

  // ── Scores. Recomputed on a ticking clock so recency decay applies. ──
  const [nowTick, setNowTick] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const i = setInterval(() => setNowTick(Math.floor(Date.now() / 1000)), 30_000);
    return () => clearInterval(i);
  }, []);

  const scoreMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of all) {
      m.set(s.aTag, scoreStream(s, signalsFor(s.aTag), nowTick));
    }
    return m;
  }, [all, signalsFor, nowTick]);

  const scoreFor = useCallback((aTag: string) => scoreMap.get(aTag) || 0, [scoreMap]);

  // ── Buckets ──
  const buckets = useMemo<DiscoveryBuckets>(() => {
    const byScore = (a: LiveStream, b: LiveStream) =>
      (scoreMap.get(b.aTag) || 0) - (scoreMap.get(a.aTag) || 0);

    const live = all.filter(s => s.isLive).sort(byScore);
    const upcoming = all
      .filter(s => s.isUpcoming)
      .sort((a, b) => (a.startsAt || Infinity) - (b.startsAt || Infinity));
    const ended = all
      .filter(s => s.hasEnded)
      .sort((a, b) => Math.max(b.endsAt, b.created_at) - Math.max(a.endsAt, a.created_at))
      .slice(0, 12);

    // Featured = highest-scoring live stream. Falls back to the soonest
    // upcoming one so the hero slot is never empty when nobody is live.
    const featured = live[0] || upcoming[0] || null;

    // Trending excludes the featured stream to avoid duplicating the hero,
    // and requires a real engagement signal so it isn't just "all live".
    const trending = live
      .filter(s => s !== featured)
      .filter(s => {
        const sig = signalsFor(s.aTag);
        return sig.chatCount > 0 || sig.tipCount > 0
          || sig.reactionCount > 0 || sig.shareCount > 0
          || s.viewerCount > 0;
      })
      .slice(0, 8);

    return { live, upcoming, ended, featured, trending, all };
  }, [all, scoreMap, signalsFor]);

  // ── Category counts for the category rail ──
  const categoryCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of all) {
      if (!s.category) continue;
      m.set(s.category.id, (m.get(s.category.id) || 0) + 1);
    }
    return m;
  }, [all]);

  // Periodic refresh so the directory stays warm.
  useEffect(() => {
    const i = setInterval(refetch, 45_000);
    return () => clearInterval(i);
  }, [refetch]);

  return {
    ...buckets,
    loading: showLoading,
    refetch,
    signalsFor,
    scoreFor,
    categoryCounts,
    totalTippedUsd,
  };
}
